import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, DialogActions, DialogContent } from '@mui/material';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { TFormType, TNoticeFormData } from 'src/@types';
import { RegUpdText } from 'src/components/custom-regUpd-text/RegUpdText';
import { useDialogContext } from 'src/components/dialog/DialogContext';
import CEditor from 'src/components/editor/CEditor';
import FormProvider, { RHFSelect, RHFTextField } from 'src/components/hook-form';
import RHFUploadDynamic from 'src/components/hook-form/RHFUploadDynamic';
import { FormInnerContainer, FormItem, FormLabel, FormRow } from 'src/layouts/form/FormLayout';
import { INotice, useStores } from 'src/models';
import { noticeApi } from 'src/service';
import { useHandleResponse } from 'src/service/useHandleResponse';
import { FormType } from 'src/static/constants/common/form';
import { getNoticeFormDefaultValues } from 'src/utils/getRHFDefaultValues';
import { separateFilesAndPaths } from 'src/utils/separateFilesAndPaths';
import * as yup from 'yup';

interface INoticeFormDialogProps {
  formType: TFormType;
  onSubmitSuccess: () => void;
  selectedData?: INotice;
}

export const NoticeFormDialog = observer(
  ({ formType, onSubmitSuccess, selectedData }: INoticeFormDialogProps) => {
    const { t } = useTranslation();
    const handleResponse = useHandleResponse();
    const { alertStore, commonCodeStore } = useStores();
    const { formId } = useDialogContext();
    const readonly = formType === FormType.READONLY;
    const isCreate = formType === FormType.CREATE;
    // 유형 select options
    const { noticeSelectOptions } = commonCodeStore;
    const noticeFormSchema = yup
      .object()
      .shape({
        prNotiCtgyCd: yup.string().required('카테고리를 선택해주세요.'),
        title: yup.string().required('제목을 입력해주세요.'),
      })
      .nullable();

    const [files, setFiles] = useState<Array<File | string>>([]);

    useEffect(() => {
      setFiles(selectedData?.filePath || []);
    }, []);

    const methods = useForm<TNoticeFormData>({
      mode: 'all',
      reValidateMode: 'onChange',
      resolver: yupResolver(noticeFormSchema),
      defaultValues: getNoticeFormDefaultValues(selectedData),
    });

    // 등록 / 수정 함수
    const onValid = async (data: TNoticeFormData) => {
      // files의 upload파일과 기존이미지path 추출
      const { files: uploadFiles, paths: existingPaths } = separateFilesAndPaths(files || []);

      // 삭제할이미지path 추출
      const originalPaths = selectedData?.filePath || [];
      const deleteFilesPath = originalPaths.filter((path) => !existingPaths.includes(path));

      const formData: TNoticeFormData = {
        title: data.title,
        descript: data.descript,
        prNotiCtgyCd: data.prNotiCtgyCd,
        ...(uploadFiles.length > 0 && { files: uploadFiles }),
        ...(existingPaths.length > 0 && { filePath: existingPaths }),
        ...(deleteFilesPath.length > 0 && { deleteFilesPath }),
      };

      const responseText = isCreate ? '등록' : '수정';
      const result = isCreate
        ? noticeApi.post(formData)
        : noticeApi.put(selectedData?.noticeSid || 0, formData);
      if (await result) {
        handleResponse(
          `${responseText}이 완료되었습니다.`,
          `${responseText}이 실패되었습니다.`,
          onSubmitSuccess,
        );
      } else {
        console.error(`${responseText} 실패`);
      }
    };

    // 삭제
    const handleDelete = () => {
      alertStore.setOnConfirm(async () => {
        if (!selectedData?.noticeSid) return;
        noticeApi
          .remove(selectedData?.noticeSid)
          .then(() =>
            handleResponse('삭제가 완료되었습니다.', '삭제가 실패되었습니다.', onSubmitSuccess),
          );
      });
      alertStore.setProps({
        open: true,
        title: '공지 삭제',
        content: '해당 공지를 삭제하시겠습니까?',
        category: 'error',
        hasCancelButton: true,
      });
    };

    return (
      <FormProvider methods={methods} onSubmit={methods.handleSubmit(onValid)} formId={formId}>
        <DialogContent dividers sx={{ pt: '10px' }}>
          <FormInnerContainer>
            <FormLabel>{t('common.form.section.basicInfo')}</FormLabel>
            <FormRow sx={{ gridTemplateColumns: '1fr 2fr' }}>
              <FormItem>
                <RHFSelect
                  label={t('notice.form.category')}
                  name="prNotiCtgyCd"
                  options={toJS(noticeSelectOptions)}
                  useAll={false}
                />
              </FormItem>
            </FormRow>
          </FormInnerContainer>
          <FormInnerContainer>
            <FormLabel>{t('common.form.section.content')}</FormLabel>
            <FormRow sx={{ gridTemplateColumns: '1fr' }}>
              <FormItem>
                <RHFTextField label={t('notice.form.title')} name="title" disabled={readonly} />
              </FormItem>
            </FormRow>
            <FormRow sx={{ gridTemplateColumns: '1fr' }}>
              <FormItem>
                <CEditor name={'descript'} placeholder={t('notice.form.desc')} />
              </FormItem>
            </FormRow>
          </FormInnerContainer>
          <FormInnerContainer>
            <FormLabel>{t('common.form.section.image')}</FormLabel>
            <FormRow sx={{ gridTemplateColumns: '1fr' }}>
              <FormItem>
                <RHFUploadDynamic
                  name="filePath"
                  files={files}
                  onChange={(newFile) => setFiles(newFile)}
                  accept={{
                    'image/jpeg': [],
                    'image/jpg': [],
                    'image/png': [],
                    'application/pdf': [], // PDF 파일
                    'application/vnd.ms-excel': [], // Excel 파일 (.xls)
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [], // Excel 파일 (.xlsx)
                    'application/msword': [], // Word 파일 (.doc)
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [], // Word 파일 (.docx)
                  }}
                />
              </FormItem>
            </FormRow>
          </FormInnerContainer>
        </DialogContent>
        {!isCreate && (
          <RegUpdText
            updDt={selectedData?.updDt ?? 0}
            userNm={selectedData?.updUser?.oprtrNm ?? '-'}
          />
        )}
        <DialogActions>
          <>
            {!isCreate && (
              <Button onClick={handleDelete} variant="contained" color="error">
                {t('common.button.delete')}
              </Button>
            )}
            <Button type="submit" variant="contained" form={formId} color="primary">
              {isCreate ? t('common.button.register') : t('common.button.edit')}
            </Button>
          </>
        </DialogActions>
      </FormProvider>
    );
  },
);
